import React, { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  keywords?: string;
  noindex?: boolean;
  structuredData?: any;
}

/**
 * SEO component that updates document head metadata
 */
export function SEO({
  title,
  description,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  keywords,
  noindex = false,
  structuredData,
}: SEOProps) {
  const siteTitle = 'SOKASTIFTELSEMEDEL.SE';
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const defaultDescription = 'Sök bland svenska stiftelser och hitta finansiering för ditt ändamål. En omfattande databas med över 17 000 stiftelser i Sverige.';
  const metaDescription = description || defaultDescription;
  const defaultImage = 'https://www.sokastiftelsemedel.se/og-image.jpg';
  const image = ogImage || defaultImage;
  const url = canonicalUrl || 'https://www.sokastiftelsemedel.se/';

  useEffect(() => {
    // Update document title
    document.title = fullTitle;
    
    // Update meta description
    let metaDescriptionTag = document.querySelector('meta[name="description"]');
    if (metaDescriptionTag) {
      metaDescriptionTag.setAttribute('content', metaDescription);
    } else {
      metaDescriptionTag = document.createElement('meta');
      metaDescriptionTag.setAttribute('name', 'description');
      metaDescriptionTag.setAttribute('content', metaDescription);
      document.head.appendChild(metaDescriptionTag);
    }
    
    // Update keywords if provided
    if (keywords) {
      let keywordsTag = document.querySelector('meta[name="keywords"]');
      if (keywordsTag) {
        keywordsTag.setAttribute('content', keywords);
      } else {
        keywordsTag = document.createElement('meta');
        keywordsTag.setAttribute('name', 'keywords');
        keywordsTag.setAttribute('content', keywords);
        document.head.appendChild(keywordsTag);
      }
    }
    
    // Update robots meta tag if noindex is true
    if (noindex) {
      let robotsTag = document.querySelector('meta[name="robots"]');
      if (robotsTag) {
        robotsTag.setAttribute('content', 'noindex, nofollow');
      } else {
        robotsTag = document.createElement('meta');
        robotsTag.setAttribute('name', 'robots');
        robotsTag.setAttribute('content', 'noindex, nofollow');
        document.head.appendChild(robotsTag);
      }
    }
    
    // Update Open Graph tags
    updateMetaTag('property', 'og:title', fullTitle);
    updateMetaTag('property', 'og:description', metaDescription);
    updateMetaTag('property', 'og:type', ogType);
    updateMetaTag('property', 'og:url', url);
    updateMetaTag('property', 'og:image', image);
    
    // Update Twitter tags
    updateMetaTag('name', 'twitter:card', 'summary_large_image');
    updateMetaTag('name', 'twitter:title', fullTitle);
    updateMetaTag('name', 'twitter:description', metaDescription);
    updateMetaTag('name', 'twitter:url', url);
    updateMetaTag('name', 'twitter:image', image);
    
    // Update canonical URL
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) {
      canonicalTag.setAttribute('href', url);
    } else {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      canonicalTag.setAttribute('href', url);
      document.head.appendChild(canonicalTag);
    }
    
    // Helper function to update meta tags
    function updateMetaTag(attributeName: string, attributeValue: string, content: string) {
      let tag = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute(attributeName, attributeValue);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    }
    
    // Add structured data if provided
    if (structuredData) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (scriptTag) {
        scriptTag.textContent = JSON.stringify(structuredData);
      } else {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        scriptTag.textContent = JSON.stringify(structuredData);
        document.head.appendChild(scriptTag);
      }
    }
    
    // Cleanup function
    return () => {
      // No cleanup needed as we're just updating existing tags
    };
  }, [fullTitle, metaDescription, keywords, noindex, ogType, url, image, structuredData]);

  // This component doesn't render anything visible
  return null;
}